import { useEffect, useLayoutEffect, useRef } from 'react';
import { useStore } from '../state/createStore';
import { currentRoute, navStore, type Route } from './navigation';

// Scroll position across screens (real-photo walkthrough 2026-10-16, real-photos/README.md #2).
//
// The document is the page's scroll owner (src/ui/scrollLock.ts), and every screen renders into the
// same document, so a screen change kept the previous screen's scroll offset: the Reports tab opened
// with its first row hidden under the header, a freshly created inspection landed mid-way down its
// overview, an area page opened at its second half. Rule:
//   push     → remember where the screen being left was, open the new screen at the top
//   back     → return to the remembered offset of the screen being returned to
//   replace / tab switch / stack reset → top
//   same screen, only a sheet / focus parameter changed (item sheet id, finding id, AI job id) → keep
// The offset is read in the store subscription — before React renders the next screen and the
// document shrinks — and applied after the new screen is in the DOM (layout effect, and again in the
// passive effect because a sheet unmounting with the old screen restores the OLD offset from its body
// lock in its passive cleanup).

export interface ScrollMemory {
  keys: string[];
  tops: number[];
}

export type ScrollPlan = { kind: 'keep' } | { kind: 'top' } | { kind: 'restore'; top: number };

// What makes a route a different page. Sheet-level parameters are left out on purpose: opening the
// item sheet or a finding editor replaces the route (so Back closes it) but the page behind stays.
export function routeKey(route: Route): string {
  switch (route.screen) {
    case 'inspection':
    case 'propertyLayout':
    case 'inspectionDetails':
    case 'bulkImport':
    case 'importChoice':
    case 'findings':
    case 'closeout':
    case 'report':
      return `${route.screen}:${route.inspectionId}`;
    case 'photoPool': {
      const focus = route.focus ? (route.focus.kind === 'area' ? `area:${route.focus.areaId}` : route.focus.kind) : 'index';
      return `photoPool:${route.inspectionId}:${focus}`;
    }
    case 'areaWorkspace':
      return `areaWorkspace:${route.inspectionId}:${route.areaId}`;
    case 'aiTasks':
      return `aiTasks:${route.inspectionId ?? ''}`;
    case 'settingsPage':
      return `settingsPage:${route.page}`;
    default:
      return route.screen;
  }
}

function sameKeys(a: readonly string[], b: readonly string[], length: number): boolean {
  for (let i = 0; i < length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

export function emptyScrollMemory(): ScrollMemory {
  return { keys: [], tops: [] };
}

// Pure decision: the memory for the new stack and what to do with the window once it has rendered.
export function planScroll(memory: ScrollMemory, nextKeys: readonly string[], currentTop: number): { memory: ScrollMemory; plan: ScrollPlan } {
  const prevDepth = memory.keys.length;
  const depth = nextKeys.length;
  if (prevDepth === 0) return { memory: { keys: [...nextKeys], tops: nextKeys.map(() => 0) }, plan: { kind: 'keep' } };
  if (depth === prevDepth && sameKeys(memory.keys, nextKeys, depth)) return { memory, plan: { kind: 'keep' } };
  if (depth > prevDepth && sameKeys(memory.keys, nextKeys, prevDepth)) {
    const tops = [...memory.tops];
    tops[prevDepth - 1] = currentTop;
    for (let i = prevDepth; i < depth; i += 1) tops.push(0);
    return { memory: { keys: [...nextKeys], tops }, plan: { kind: 'top' } };
  }
  if (depth < prevDepth && sameKeys(memory.keys, nextKeys, depth)) {
    return { memory: { keys: memory.keys.slice(0, depth), tops: memory.tops.slice(0, depth) }, plan: { kind: 'restore', top: memory.tops[depth - 1] ?? 0 } };
  }
  return { memory: { keys: [...nextKeys], tops: nextKeys.map(() => 0) }, plan: { kind: 'top' } };
}

// The document offset even while an overlay has the body pinned (position:fixed; top = -scrollY).
export function currentScrollTop(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const body = document.body;
  if (body && body.style.position === 'fixed') {
    const pinned = parseFloat(body.style.top);
    if (Number.isFinite(pinned)) return Math.max(0, -pinned);
  }
  return window.scrollY || 0;
}

export function useScrollRestoration(): void {
  const memory = useRef<ScrollMemory>(emptyScrollMemory());
  const pending = useRef<ScrollPlan | null>(null);
  const route = useStore(navStore, currentRoute);

  useEffect(() => {
    memory.current = planScroll(memory.current, navStore.get().stack.map(routeKey), currentScrollTop()).memory;
    return navStore.subscribe(() => {
      const result = planScroll(memory.current, navStore.get().stack.map(routeKey), currentScrollTop());
      memory.current = result.memory;
      if (result.plan.kind !== 'keep') pending.current = result.plan;
    });
  }, []);

  const apply = (clear: boolean) => {
    const plan = pending.current;
    if (!plan || typeof window === 'undefined') return;
    window.scrollTo(0, plan.kind === 'restore' ? plan.top : 0);
    if (clear) pending.current = null;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per route change
  useLayoutEffect(() => apply(false), [route]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per route change
  useEffect(() => apply(true), [route]);
}
